import type { CSSProperties } from 'react';

/**
 * The module masthead — one slim bar that sits flush under the shell's top bar.
 *
 * Same recipe as the Asset Management header (asset-management/constants/theme.ts
 * ASSET_HEADER_GRADIENT), lifted here so other modules can share it without
 * importing Asset Management's stylesheet: deep slate -> indigo -> violet, full
 * width, square corners, no top margin. The shell content wrapper carries no top
 * padding, so a page whose first child is this bar touches the top bar.
 *
 * `isolate` makes the bar a stacking context so the bloom layer (z -1) stays
 * behind its content. The same consequence as the asset header applies: a
 * dropdown inside the bar cannot rise above the page with an inner z-index — give
 * the bar itself `z-20` at the call site if it holds one.
 */
export const MODULE_HEADER_BASE =
  'relative isolate flex-shrink-0 border-b border-white/10 shadow-[0_8px_24px_-16px_rgba(15,23,42,0.8)]';

/**
 * The one header surface every module and sub-module uses: a light cream card in
 * light mode, a bar in the brand palette's gradient in dark mode.
 * Defined once in index.css (MODULE HEADER block) — change it there and every
 * module header follows. Write header text white: light mode turns it to ink inside the card.
 *
 * MODULE_HEADER_BG is the CSS value (inline `style={{ background }}`,
 * ModuleBanner.background); MODULE_HEADER_BG_CLASS is the class. MUI `sx`
 * headers: colour text with var(--theme-header-fg), which is white on the bar.
 */
export const MODULE_HEADER_BG = 'var(--module-header-bg)';
export const MODULE_HEADER_BG_CLASS = 'module-header';

/** The base shape plus the shared orange — the default header bar. */
export const MODULE_HEADER_BAR = `${MODULE_HEADER_BASE} ${MODULE_HEADER_BG_CLASS}`;

/** Inner row padding — the light card is ~64px tall. */
export const MODULE_HEADER_INNER = 'px-4 sm:px-5 py-3';

/** Icon tile on the left of the title — solid palette colour in light mode (index.css). */
export const MODULE_HEADER_ICON =
  'module-header-icon w-10 h-10 rounded-xl bg-white/10 ring-1 ring-inset ring-white/20 flex items-center justify-center flex-shrink-0';

export const MODULE_HEADER_TITLE = 'text-[16px] sm:text-[18px] font-bold text-white tracking-tight truncate leading-tight';
export const MODULE_HEADER_SUBTITLE =
  'text-[10.5px] font-bold uppercase tracking-[0.12em] text-[color:var(--brand-deep)] dark:text-white/70 leading-tight mt-0.5 hidden sm:block';

/** Ghost button on the dark bar (icon or label). */
export const MODULE_HEADER_GHOST_BTN =
  'inline-flex items-center justify-center gap-1.5 h-7 px-2.5 rounded-md bg-white/10 ring-1 ring-inset ring-white/20 text-white text-xs font-semibold hover:bg-white/20 transition-colors';

/** Primary (white) button on the dark bar. */
export const MODULE_HEADER_PRIMARY_BTN =
  'inline-flex items-center gap-1.5 h-7 px-3 rounded-md bg-white text-[#5a3d08] text-xs font-semibold shadow-sm hover:bg-amber-50 transition-colors flex-shrink-0';

/** The two soft radial blooms behind the bar — render as an absolutely-positioned span. */
export const MODULE_HEADER_BLOOM_STYLE: CSSProperties = {
  position: 'absolute',
  inset: 0,
  zIndex: -1,
  pointerEvents: 'none',
  background:
    'radial-gradient(60% 180% at 88% 0%, rgba(240,189,69,0.30) 0%, rgba(240,189,69,0) 60%),' +
    'radial-gradient(50% 160% at 12% 100%, rgba(20,14,4,0.10) 0%, rgba(20,14,4,0) 65%)',
};
